import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { Switch, useHistory, useLocation, useRouteMatch } from 'react-router';
import { toast } from 'sonner';
import {
	IContentCalendarSelectedSuggestion,
	IImpersonationContextComponentProps,
	ILocationState,
	IModalContext,
	IMultiCampaignSchedulerRequest,
	IMultiCampaignSchedulerResult,
	ImpersonationContextKey,
	ModalChildComponentContextKey,
} from '../../../../../../models';
import { CampaignType } from '../../../../../../models/AdminModels';
import { shouldShowDuplicateTemplateWarning } from '../../../../../../models/Campaigns';
import { useErrorMessages, useUserSession } from '../../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../../models/Logging';
import {
	accountHasComplianceEnabled,
	getSocialMediaPlainTextContentFromRawRichContent,
	getUniqueIdForSuggestion,
} from '../../../../../../models/UiUtils';
import { hasAnyBulkAiEligibleSuggestion } from '../../../../../../models/utils/contentCalendarBulkAiUtils';
import {
	useAccountProfileQuery,
	useBrandingSettingsQuery,
	useConvertSocialVideoMutation,
	useTemplateQueries,
	useUserQuery,
} from '../../../../../../queries';
import { hasBrandingSettings } from '../../../../../../utils/hasBrandingSettings';
import { hasContentPreferences } from '../../../../../../utils/hasContentPreferences';
import { AdvancedOptionsButton } from '../../../../../components/automation/AutomationStepCard/presentation';
import { useCampaignCalendarSuggestionsContext } from '../../../../../components/campaigns/CampaignCalendar/context';
import { ComplianceApprovalPrompt } from '../../../../../components/campaigns/ComplianceApprovalPrompt';
import { ISelectOption } from '../../../../../components/DeprecatedSelect';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import { PrivateRoute } from '../../../../../components/PrivateRoute';
import { InfoIcon } from '../../../../../components/svgs/icons/InfoIcon';
import { getTemplateApprovalStatus } from '../../../../../components/TemplateReviewStatusIndicator';
import { PopoverType, TinyPopover } from '../../../../../components/TinyPopover';
import { Toggle } from '../../../../../components/Toggle';
import { AiButton } from '../../../../../components-v2/AiButton';
import { Button } from '../../../../../components-v2/Button';
import { Collapsible, CollapsiblePanel } from '../../../../../components-v2/Collapsible';
import { UnsavedChangesWarning } from '../../../../../components-v2/UnsavedChangesWarning';
import { charmGray } from '../../../../../styles/colors';
import { bs } from '../../../../../styles/styles';
import { useHandleAiGeneration } from '../../../hooks/handleAiGeneration';
import { WarningBanner, WarningBannerMessage } from '../../../presentation';
import { MultiSchedulerAiModal } from '../../aiModal';
import { MultiCampaignSchedulerLayout } from '../../MultiCampaignSchedulerLayout';
import { MultiCampaignTemplateItem } from '../../MultiCampaignTemplateItem';
import { useScheduleSocialMediaSuggestions, useSocialMediaTargetOptions } from './hooks';
import { ResetAllEditedContentModal } from './modals/ResetAllEditedContentModal';
import { ResetSuggestionEditedContentModal } from './modals/ResetSuggestionEditedContentModal';
import {
	EditableSocialMediaTemplateItem,
	MultiCampaignSocialMediaConnection,
	SocialMediaPostPreviewSelector,
	SocialMediaTemplateItem,
} from './presentation';
import { styleSheet } from './styles';
import {
	areAttachmentRowsEqual,
	getBrandingBatchForAttachments,
	type PostBrandingBatch,
	useParallelBrandingJobs,
} from './useParallelBrandingJobs';

type SendFromOptions = {
	sendFrom: Api.ISendEmailFrom;
	sendFromUserId: string | null;
};

interface MultiSocialSchedulerProps extends IImpersonationContextComponentProps, IModalContext {
	styles?: StyleDeclarationValue[];
}

function _MultiSocialScheduler({ styles = [], parentModal, impersonationContext }: MultiSocialSchedulerProps) {
	const { url } = useRouteMatch();
	const history = useHistory();
	const location = useLocation<ILocationState<any, IMultiCampaignSchedulerRequest>>();
	const isSequenceStep = location.state?.model?.isSequenceStep;
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const accountProfileQuery = useAccountProfileQuery({ impersonationContext });
	const brandingSettingsQuery = useBrandingSettingsQuery({ impersonationContext });
	const accountHasBrandingSettings = hasBrandingSettings(brandingSettingsQuery.data);
	const [editedVideoJobs, setEditedVideoJobs] = React.useState<Record<string, string>>({});
	const [isResetEditedContentModalOpen, setIsResetEditedContentModalOpen] = React.useState(false);
	const [suggestionIdForReset, setSuggestionIdForReset] = React.useState<string | null>(null);

	const { logApiError } = useEventLogging('MultiSocialScheduler');
	const requiresCompliance = accountHasComplianceEnabled(userSession, impersonationContext);
	const [advancedOptionsToggled, setAdvancedOptionsToggled] = React.useState(false);
	const account = impersonationContext?.isValid ? impersonationContext.account : userSession.account?.toJs();
	const [overlayLogo, setOverlayLogo] = React.useState(() => {
		return account?.features.socialMedia.enabled && account?.features.socialMedia.alwaysOverlayLogo;
	});
	const [logoSizes, setLogoSizes] = React.useState<Record<string, number>>({});
	const [previewedSuggestionId, setPreviewedSuggestionId] = React.useState<string | null>(null);

	const hideWhiteBackground = React.useMemo(() => {
		return account?.features.socialMedia.enabled && account?.features.socialMedia.hideWhiteBackground;
	}, [account?.features.socialMedia.enabled, account?.features.socialMedia.hideWhiteBackground]);
	const [generatedContent, setGeneratedContent] = React.useState<Record<string, Api.IRawRichTextContentState>>({});
	const [generatedAiReferences, setGeneratedAiReferences] = React.useState<Record<string, Api.IAIReference>>({});

	const { pendingItems, queueItemForAiGeneration, isGenerating, processQueue, suggestionsWithErrors } =
		useHandleAiGeneration({
			contentType: 'social',
			impersonationContext,
			onAiGenerationComplete: (
				suggestion: IContentCalendarSelectedSuggestion,
				toolCall: Api.SocialMediaToolCall,
				conversationId: string
			) => {
				const key = getUniqueIdForSuggestion(suggestion);
				setGeneratedContent(prev => ({ ...prev, [key]: toolCall.content }));
				setGeneratedAiReferences(prev => ({ ...prev, [key]: { sessionId: conversationId } }));
			},
		});
	const videoPreviewRef = React.useRef<HTMLVideoElement | null>(null);

	const [showingOriginalContent, setShowingOriginalContent] = React.useState<string[]>([]);

	// State for editing suggestions
	const [editingSuggestionId, setEditingSuggestionId] = React.useState<string | null>(null);
	const [editedContent, setEditedContent] = React.useState<Record<string, Api.IRawRichTextContentState>>({});
	const [editedImages, setEditedImages] = React.useState<Record<string, Api.IFileAttachment[]>>({});
	/** Attachment IDs sent to branding; set on init and user image edits, not when a job returns (re-rebrand reuses same sources). */
	const [rebrandSourceBatchBySuggestion, setRebrandSourceBatchBySuggestion] = React.useState<
		Record<string, PostBrandingBatch | null>
	>({});
	/** Full attachment rows to restore on “Undo branding” (in sync with rebrand source id batch). */
	const [rebrandSourceFilesBySuggestion, setRebrandSourceFilesBySuggestion] = React.useState<
		Record<string, Api.IFileAttachment[]>
	>({});
	/** Snapshot when opening edit so Cancel restores bulk-branded images and prior text, not template-only defaults */
	const editSessionBaselineRef = React.useRef<{
		suggestionId: string;
		hadEditedContent: boolean;
		editedContentSnapshot: Api.IRawRichTextContentState | undefined;
		hadEditedImages: boolean;
		editedImagesSnapshot: Api.IFileAttachment[] | undefined;
	} | null>(null);

	const {
		selectedSuggestions,
		deselectSuggestion,
		updateStartDateForSuggestion,
		getStartDateForSuggestion,
		onScheduleSuccess,
	} = useCampaignCalendarSuggestionsContext();

	const { mutate: convertVideoMutation } = useConvertSocialVideoMutation();

	const [sendFromOptions, setSendFromOptions] = React.useState<SendFromOptions>(() => {
		let sendFrom: Api.ISendEmailFrom;
		let sendFromUserId: string = null;
		if (impersonationContext?.isValid) {
			sendFrom = Api.SendEmailFrom.SelectedUser;
			sendFromUserId = impersonationContext.user?.id || null;
		} else {
			sendFrom = Api.SendEmailFrom.CurrentUser;
			sendFromUserId = userSession.user.id;
		}

		return {
			sendFrom,
			sendFromUserId,
		};
	});

	const authorId =
		sendFromOptions.sendFrom === Api.SendEmailFrom.SelectedUser ? sendFromOptions.sendFromUserId : userSession.user.id;
	const impersonationJs = React.useMemo(() => impersonationContext?.toJs(), [impersonationContext]);

	const userQuery = useUserQuery({
		id: authorId,
		enabled: Boolean(authorId),
		impersonationContext: impersonationJs,
	});
	const selectedUser = userQuery.data;

	const templateQueries = useTemplateQueries({
		impersonationContext: impersonationJs,
		templateIds: selectedSuggestions.map((t: IContentCalendarSelectedSuggestion) => t.templateReference.templateId),
		refetchOnWindowFocus: false,
		queryKeyProvider: (_, index) => {
			const suggestion = selectedSuggestions[index];
			return getUniqueIdForSuggestion(suggestion);
		},
	});

	React.useEffect(() => {
		setRebrandSourceBatchBySuggestion(prev => {
			let next: Record<string, PostBrandingBatch | null> | null = null;
			for (let i = 0; i < selectedSuggestions.length; i++) {
				const q = templateQueries[i];
				if (!q?.isSuccess) {
					continue;
				}
				const sid = getUniqueIdForSuggestion(selectedSuggestions[i]);
				if (Object.prototype.hasOwnProperty.call(prev, sid)) {
					continue;
				}
				const att = editedImages[sid] ?? q.data.attachments ?? [];
				const batch = getBrandingBatchForAttachments(att);
				if (!next) {
					next = { ...prev };
				}
				next[sid] = batch;
			}
			return next ?? prev;
		});
		setRebrandSourceFilesBySuggestion(prev => {
			let next: Record<string, Api.IFileAttachment[]> | null = null;
			for (let i = 0; i < selectedSuggestions.length; i++) {
				const q = templateQueries[i];
				if (!q?.isSuccess) {
					continue;
				}
				const sid = getUniqueIdForSuggestion(selectedSuggestions[i]);
				if (Object.prototype.hasOwnProperty.call(prev, sid)) {
					continue;
				}
				const att = editedImages[sid] ?? q.data.attachments ?? [];
				if (!next) {
					next = { ...prev };
				}
				next[sid] = [...att];
			}
			return next ?? prev;
		});
	}, [editedImages, selectedSuggestions, templateQueries]);

	/** One batch per post (scheduler row); same attachment may appear in multiple batches if reused across posts. */
	const bulkRebrandPostBatches = React.useMemo(() => {
		const batches: { attachmentIds: string[] }[] = [];
		for (let i = 0; i < selectedSuggestions.length; i++) {
			const templateQuery = templateQueries[i];
			if (!templateQuery?.isSuccess) {
				continue;
			}
			const suggestion = selectedSuggestions[i];
			const suggestionId = getUniqueIdForSuggestion(suggestion);
			const fromSource = rebrandSourceBatchBySuggestion[suggestionId];
			const batch =
				fromSource !== undefined
					? fromSource
					: getBrandingBatchForAttachments(editedImages[suggestionId] ?? templateQuery.data.attachments ?? []);
			if (batch) {
				batches.push(batch);
			}
		}
		return batches;
	}, [editedImages, rebrandSourceBatchBySuggestion, selectedSuggestions, templateQueries]);

	const bulkRebrandImageCount = React.useMemo(
		() => bulkRebrandPostBatches.reduce((n, b) => n + b.attachmentIds.length, 0),
		[bulkRebrandPostBatches]
	);

	const postsWithUndoableBrandingCount = React.useMemo(() => {
		let n = 0;
		for (let i = 0; i < selectedSuggestions.length; i++) {
			const q = templateQueries[i];
			if (!q?.isSuccess) {
				continue;
			}
			const sid = getUniqueIdForSuggestion(selectedSuggestions[i]);
			const snap = rebrandSourceFilesBySuggestion[sid];
			if (!snap?.length) {
				continue;
			}
			const current = editedImages[sid] ?? q.data.attachments ?? [];
			if (!areAttachmentRowsEqual(current, snap)) {
				n++;
			}
		}
		return n;
	}, [editedImages, rebrandSourceFilesBySuggestion, selectedSuggestions, templateQueries]);

	const applyBrandedSourceToEditedImages = React.useCallback(
		(sourceAttachmentId: string, brandedAttachment: Api.IFileAttachment) => {
			setEditedImages(prev => {
				const next = { ...prev };
				let changed = false;
				for (let i = 0; i < selectedSuggestions.length; i++) {
					const q = templateQueries[i];
					if (!q?.isSuccess) {
						continue;
					}
					const suggestion = selectedSuggestions[i];
					const sid = getUniqueIdForSuggestion(suggestion);
					const current = prev[sid] ?? q.data.attachments ?? [];
					if (!current.length) {
						continue;
					}
					const replaced = current.map(att => (att.id === sourceAttachmentId ? brandedAttachment : att));
					const rowChanged = replaced.some((a, idx) => a.id !== current[idx]?.id);
					if (rowChanged) {
						next[sid] = replaced;
						changed = true;
					}
				}
				return changed ? next : prev;
			});
		},
		[selectedSuggestions, templateQueries]
	);

	const {
		startBranding: startParallelBranding,
		retryFailed: retryParallelBrandingFailed,
		conversationReady: parallelBrandingConversationReady,
		isBusy: isParallelBrandingBusy,
		failedCount: parallelBrandingFailedCount,
		successCount: parallelBrandingSuccessCount,
		sourcePhase: parallelBrandingSourcePhase,
	} = useParallelBrandingJobs({
		impersonationContext,
		onBranded: applyBrandedSourceToEditedImages,
	});

	const parallelBrandingBatchTotal = React.useMemo(
		() => Object.keys(parallelBrandingSourcePhase).length,
		[parallelBrandingSourcePhase]
	);

	const scheduledPostsRef = React.useRef<Map<string, Api.ISocialMediaPost>>(new Map());
	const suggestionPostErrorsRef = React.useRef<Set<[IContentCalendarSelectedSuggestion, Api.IOperationResultNoValue]>>(
		new Set()
	);
	const scheduleMutation = useScheduleSocialMediaSuggestions({
		onSuccess: (results, params) => {
			results.forEach((result, i) => {
				const suggestion = params.suggestions[i];
				if (result.status === 'fulfilled') {
					const socialPost = result.value;
					scheduledPostsRef.current.set(getUniqueIdForSuggestion(suggestion), socialPost);

					if (editedVideoJobs[getUniqueIdForSuggestion(suggestion)]) {
						convertVideoMutation({
							jobId: editedVideoJobs[getUniqueIdForSuggestion(suggestion)],
							postId: socialPost.id,
							forUserId: authorId,
							impersonationContext,
						});
					}
				} else {
					const error = Api.asApiError(result.reason);
					const matchingSuggestion = selectedSuggestions.find(
						(s: IContentCalendarSelectedSuggestion) =>
							getUniqueIdForSuggestion(suggestion) === getUniqueIdForSuggestion(s)
					);
					suggestionPostErrorsRef.current.add([matchingSuggestion, error]);
				}
			});

			if (scheduledPostsRef.current.size === selectedSuggestions.length) {
				const scheduled = Array.from(scheduledPostsRef.current?.values()) || [];
				const scheduleResult: IMultiCampaignSchedulerResult = {
					scheduledSuggestions: selectedSuggestions,
					withCompliance: params.sendWithCompliance,
				};

				toast.success(`${scheduled.length} social posts scheduled.`);

				const event = onScheduleSuccess(scheduleResult);
				if (!event.defaultPrevented && !isSequenceStep) {
					parentModal.onRequestClose();
				}
			} else {
				toast.error(
					'An error occurred while scheduling some of the posts. If you retry we will only schedule the posts that were unsuccessfully scheduled.'
				);
			}
		},
		onError: (error: Api.IOperationResultNoValue) => {
			logApiError('MultiCampaignSocialScheduler-Error', error as Api.IOperationResultNoValue);
			errorMessages.pushApiError(error as Api.IOperationResultNoValue);
			toast.error('An error occurred while scheduling the posts.');
		},
	});

	const { facebookOptions, instagramOptions, linkedInOptions } = useSocialMediaTargetOptions({ user: selectedUser });
	const socialMediaOptions: {
		options: ISelectOption<Api.ISocialMediaConnection>[];
		provider: Api.SocialMediaType;
	}[] = React.useMemo(() => {
		return [
			{ options: facebookOptions, provider: Api.SocialMediaType.Facebook },
			{ options: instagramOptions, provider: Api.SocialMediaType.Instagram },
			{ options: linkedInOptions, provider: Api.SocialMediaType.LinkedIn },
		];
	}, [facebookOptions, instagramOptions, linkedInOptions]);

	React.useEffect(() => {
		if (!selectedUser) {
			return;
		}
		/**
		 * @If the user has a connected account, select the first connected account for each provider
		 * This is to ensure that the user doesn't have to manually select the connected account
		 */
		let newConnectionOptions: ISelectOption<Api.ISocialMediaConnection>[] = [];
		socialMediaOptions.forEach(({ options }) => {
			const connectedAccounts = options.filter(
				option => option.dataContext?.state === Api.SocialMediaConnectionState.Connected
			);
			if (options.length > 0 && connectedAccounts.length) {
				newConnectionOptions = newConnectionOptions.concat(connectedAccounts);
			}
		});
		setSelectedConnectionOptions(newConnectionOptions);
	}, [selectedUser, socialMediaOptions, sendFromOptions]);

	const [selectedConnectionOptions, setSelectedConnectionOptions] = React.useState<
		ISelectOption<Api.ISocialMediaConnection>[]
	>([]);

	const hasSelectedDisconnectedConnection = selectedConnectionOptions.some(option => {
		return option?.dataContext?.state !== Api.SocialMediaConnectionState.Connected;
	});

	const suggestionsWithoutDatesCount = selectedSuggestions.filter(
		(s: IContentCalendarSelectedSuggestion) => !getStartDateForSuggestion(s)
	).length;

	// Handlers for editing
	const handleEdit = (suggestion: IContentCalendarSelectedSuggestion) => {
		const id = getUniqueIdForSuggestion(suggestion);
		const hadEditedContent = Object.prototype.hasOwnProperty.call(editedContent, id);
		const hadEditedImages = Object.prototype.hasOwnProperty.call(editedImages, id);
		editSessionBaselineRef.current = {
			suggestionId: id,
			hadEditedContent,
			editedContentSnapshot: editedContent[id],
			hadEditedImages,
			editedImagesSnapshot: hadEditedImages ? [...(editedImages[id] ?? [])] : undefined,
		};
		setEditingSuggestionId(id);
		// Initialize edited content/images if not already set (do not replace existing editedImages — e.g. bulk branding)
		if (!editedContent[id]) {
			const template = templateQueries.find((q, idx) => selectedSuggestions[idx] === suggestion)?.data;
			if (template) {
				// Prioritize generated content over template content if it exists
				const contentToUse = generatedContent[id] || template.content;
				setEditedContent(prev => ({ ...prev, [id]: contentToUse }));
				if (!hadEditedImages) {
					setEditedImages(prev => ({ ...prev, [id]: template.attachments || [] }));
				}
			}
		} else if (!hadEditedImages) {
			const template = templateQueries.find((q, idx) => selectedSuggestions[idx] === suggestion)?.data;
			if (template) {
				setEditedImages(prev => ({ ...prev, [id]: template.attachments || [] }));
			}
		}
	};

	const handleCancelEdit = (suggestion: IContentCalendarSelectedSuggestion) => {
		const id = getUniqueIdForSuggestion(suggestion);
		const baseline = editSessionBaselineRef.current;
		setEditingSuggestionId(null);
		editSessionBaselineRef.current = null;

		if (!baseline || baseline.suggestionId !== id) {
			if (process.env.NODE_ENV !== 'production') {
				console.warn('[handleCancelEdit] baseline mismatch — branded images may be lost', { baseline, id });
			}
			setEditedContent(prev => {
				const next = { ...prev };
				delete next[id];
				return next;
			});
			setEditedImages(prev => {
				const next = { ...prev };
				delete next[id];
				return next;
			});
			setRebrandSourceBatchBySuggestion(prev => {
				const next = { ...prev };
				delete next[id];
				return next;
			});
			setRebrandSourceFilesBySuggestion(prev => {
				const next = { ...prev };
				delete next[id];
				return next;
			});
			return;
		}

		const rowIndex = selectedSuggestions.findIndex(s => getUniqueIdForSuggestion(s) === id);
		const templateAttachments = templateQueries[rowIndex]?.data?.attachments;
		const rebrandFileBaseline = baseline.hadEditedImages
			? (baseline.editedImagesSnapshot ?? [])
			: (templateAttachments ?? []);

		setEditedContent(prev => {
			const next = { ...prev };
			if (baseline.hadEditedContent && baseline.editedContentSnapshot !== undefined) {
				next[id] = baseline.editedContentSnapshot;
			} else {
				delete next[id];
			}
			return next;
		});
		setEditedImages(prev => {
			const next = { ...prev };
			if (baseline.hadEditedImages) {
				next[id] = baseline.editedImagesSnapshot ?? [];
			} else {
				delete next[id];
			}
			return next;
		});
		setRebrandSourceBatchBySuggestion(prev => ({
			...prev,
			[id]: getBrandingBatchForAttachments(
				baseline.hadEditedImages ? baseline.editedImagesSnapshot : templateAttachments
			),
		}));
		setRebrandSourceFilesBySuggestion(prev => ({ ...prev, [id]: [...rebrandFileBaseline] }));
	};

	const handleSaveEdit = (suggestion: IContentCalendarSelectedSuggestion) => {
		const id = getUniqueIdForSuggestion(suggestion);
		const rowIndex = selectedSuggestions.findIndex(s => getUniqueIdForSuggestion(s) === id);
		const templateAtt = templateQueries[rowIndex]?.data?.attachments;
		const fileRow = editedImages[id] ?? templateAtt ?? [];
		setRebrandSourceBatchBySuggestion(prev => ({
			...prev,
			[id]: getBrandingBatchForAttachments(fileRow),
		}));
		setRebrandSourceFilesBySuggestion(prev => ({ ...prev, [id]: [...fileRow] }));
		editSessionBaselineRef.current = null;
		setEditingSuggestionId(null);
	};

	const handleContentChange = (
		suggestion: IContentCalendarSelectedSuggestion,
		content: Api.IRawRichTextContentState
	) => {
		const id = getUniqueIdForSuggestion(suggestion);
		setEditedContent(prev => ({ ...prev, [id]: content }));
	};

	const handleImageSelected = (image: Api.IFileAttachment) => {
		if (!editingSuggestionId) return;
		const row = image ? [image] : [];
		setEditedImages(prev => ({ ...prev, [editingSuggestionId]: row }));
		setRebrandSourceBatchBySuggestion(prev => ({
			...prev,
			[editingSuggestionId]: getBrandingBatchForAttachments(row),
		}));
		setRebrandSourceFilesBySuggestion(prev => ({ ...prev, [editingSuggestionId]: [...row] }));
	};

	const handleUndoBrandingForPost = React.useCallback(
		(suggestion: IContentCalendarSelectedSuggestion) => {
			const id = getUniqueIdForSuggestion(suggestion);
			const snap = rebrandSourceFilesBySuggestion[id];
			if (snap === undefined) {
				return;
			}
			const rowIndex = selectedSuggestions.findIndex(s => getUniqueIdForSuggestion(s) === id);
			const templateAtt = templateQueries[rowIndex]?.data?.attachments ?? [];
			setEditedImages(prev => {
				const next = { ...prev };
				if (areAttachmentRowsEqual(snap, templateAtt)) {
					delete next[id];
				} else {
					next[id] = [...snap];
				}
				return next;
			});
		},
		[rebrandSourceFilesBySuggestion, selectedSuggestions, templateQueries]
	);

	const handleUndoBrandingForAll = React.useCallback(() => {
		setEditedImages(prev => {
			let next: Record<string, Api.IFileAttachment[]> | null = null;
			for (let i = 0; i < selectedSuggestions.length; i++) {
				const q = templateQueries[i];
				if (!q?.isSuccess) {
					continue;
				}
				const sid = getUniqueIdForSuggestion(selectedSuggestions[i]);
				const snap = rebrandSourceFilesBySuggestion[sid];
				if (snap === undefined) {
					continue;
				}
				const templateAtt = q.data?.attachments ?? [];
				const current = prev[sid] ?? templateAtt;
				if (areAttachmentRowsEqual(current, snap)) {
					continue;
				}
				if (!next) {
					next = { ...prev };
				}
				if (areAttachmentRowsEqual(snap, templateAtt)) {
					delete next[sid];
				} else {
					next[sid] = [...snap];
				}
			}
			return next ?? prev;
		});
	}, [rebrandSourceFilesBySuggestion, selectedSuggestions, templateQueries]);

	const handleVideoSelected = (video: HTMLVideoElement, jobId: string) => {
		videoPreviewRef.current = video;
		setEditedVideoJobs(prev => ({ ...prev, [editingSuggestionId]: jobId }));
	};

	/** Shared with the welcome modal and toolbar so bulk AI never skips the reset-confirmation when there are local edits. */
	const queueBulkAiOrOpenResetForEditedContent = React.useCallback(() => {
		const hasBulkEditedContent = Object.keys(editedContent).length > 0 || Object.keys(editedImages).length > 0;
		if (hasBulkEditedContent) {
			setIsResetEditedContentModalOpen(true);
		} else {
			selectedSuggestions.forEach((suggestion: IContentCalendarSelectedSuggestion) =>
				queueItemForAiGeneration(suggestion, { forBulkAction: true })
			);
			processQueue();
		}
	}, [editedContent, editedImages, selectedSuggestions, queueItemForAiGeneration, processQueue]);

	const scheduleSuggestions = (sendWithCompliance = false, sendWithComplianceEmail?: string) => {
		// Filter out suggestions that have already been successfully scheduled
		const unscheduledSuggestionsAndTemplates = selectedSuggestions
			.map((suggestion: IContentCalendarSelectedSuggestion, i: number) => ({
				suggestion,
				templateQuery: templateQueries[i],
			}))
			.filter(
				({
					suggestion,
					templateQuery,
				}: {
					suggestion: IContentCalendarSelectedSuggestion;
					templateQuery: ReturnType<typeof useTemplateQueries>[number];
				}) => {
					const id = getUniqueIdForSuggestion(suggestion);
					// Only include if not already scheduled and template data is available
					return !scheduledPostsRef.current.has(id) && templateQuery?.data;
				}
			);

		const unscheduledSuggestions = unscheduledSuggestionsAndTemplates.map(
			({ suggestion }: { suggestion: IContentCalendarSelectedSuggestion }) => suggestion
		);
		const unscheduledTemplateQueries = unscheduledSuggestionsAndTemplates.map(
			({ templateQuery }: { templateQuery: ReturnType<typeof useTemplateQueries>[number] }) => templateQuery
		);

		// Use edited content/images if available, otherwise use template defaults
		const templatesWithEdits = unscheduledTemplateQueries.map(
			(t: ReturnType<typeof useTemplateQueries>[number], i: number) => {
				const suggestion = unscheduledSuggestions[i];
				const id = getUniqueIdForSuggestion(suggestion);
				const editedContentForSuggestion = editedContent[id];
				const editedImagesForSuggestion = editedImages[id];

				if (editedContentForSuggestion || editedImagesForSuggestion) {
					return {
						...t.data,
						content: editedContentForSuggestion || t.data.content,
						attachments: editedImagesForSuggestion || t.data.attachments,
					};
				}
				return t.data;
			}
		);

		scheduleMutation.mutate({
			impersonationContext: impersonationContext?.toJs(),
			forUserId: authorId,
			templates: templatesWithEdits,
			suggestions: unscheduledSuggestions.map((suggestion: IContentCalendarSelectedSuggestion) => {
				return {
					...suggestion,
					schedule: {
						startDate: getStartDateForSuggestion(suggestion).toISOString(),
					},
					overlayLogo,
				} as IContentCalendarSelectedSuggestion;
			}),
			generatedContent,
			generatedAiReferences,
			editedContent,
			logoSizes,
			sendWithCompliance,
			sendWithComplianceEmail,
			designatedTargets: selectedConnectionOptions
				.map(connectionOption => {
					const connection = connectionOption.dataContext;
					if (connection?.postTargetId && connection?.userId) {
						return {
							provider: connection.type,
							pageId: connection.postTargetId,
							userId: connection.userId,
						};
					}
					return null;
				})
				.filter(Boolean),
		});
	};

	const canSchedule: boolean =
		suggestionsWithoutDatesCount === 0 &&
		!hasSelectedDisconnectedConnection &&
		!scheduleMutation.isPending &&
		!templateQueries.some(q => q.isPending) &&
		Object.values(selectedConnectionOptions).some(
			option => option?.dataContext?.state === Api.SocialMediaConnectionState.Connected
		) &&
		Boolean(impersonationContext?.isValid ? impersonationContext.user?.id : true);

	return (
		<div className={css(styleSheet.container, ...styles)}>
			{hasSelectedDisconnectedConnection ? (
				<WarningBanner>
					{selectedConnectionOptions.map(selectedConnectionOption => {
						const connection = selectedConnectionOption.dataContext;
						const provider = connection.type;
						if (connection && connection.state !== Api.SocialMediaConnectionState.Connected) {
							// TODO: update this for other states (e.g. no target)?
							return (
								<WarningBannerMessage key={connection.postTargetId}>
									<span>{`${Api.SocialMediaType[provider]} account: `}</span>
									<strong>{`@${connection?.postTargetDisplayName || connection?.userName}`}</strong>
									{` was disconnected ${moment(connection.expiresAt).format(
										'MM/DD/YYYY'
									)}. Please reconnect the account to schedule content on this platform.`}
								</WarningBannerMessage>
							);
						}
						return null;
					})}
				</WarningBanner>
			) : null}
			{suggestionsWithoutDatesCount > 0 ? (
				<WarningBanner>
					<>
						<WarningBannerMessage>
							{`You have `}
							<strong>{suggestionsWithoutDatesCount}</strong>
							{` without a send date. Please select a date/time to continue scheduling these posts.`}
						</WarningBannerMessage>
					</>
				</WarningBanner>
			) : null}
			<MultiCampaignSchedulerLayout
				{...sendFromOptions}
				hideContactOwnerOption
				title='Schedule Social Media Campaigns'
				onSendFromChange={(option, user) => {
					impersonationContext?.updateUser({
						id: option.sender.mode === Api.SendEmailFrom.SelectedUser ? user.id : userSession.user.id,
					});
					setSendFromOptions({ sendFrom: option.sender.mode, sendFromUserId: user?.id });
				}}
				styles={[styleSheet.scheduler]}
				isSocialMediaLayout
				sideBar={
					<Switch>
						<PrivateRoute path={`${url}/compliance-options`} userSession={userSession}>
							<ComplianceApprovalPrompt
								disabled={!canSchedule}
								isLoading={scheduleMutation.isPending}
								campaignType={CampaignType.Social}
								onScheduleCompliance={(e: React.MouseEvent<HTMLElement>, sendWithComplianceEmail: string) => {
									e.preventDefault();
									scheduleSuggestions(true, sendWithComplianceEmail);
								}}
								onScheduleWithoutCompliance={() => scheduleSuggestions()}
							/>
						</PrivateRoute>
						<PrivateRoute userSession={userSession}>
							<SocialMediaPostPreviewSelector
								templateQueries={templateQueries}
								selectedSuggestions={selectedSuggestions}
								generatedContent={generatedContent}
								editedContent={editedContent}
								editedImages={editedImages}
								overlayLogo={overlayLogo}
								impersonationCtx={impersonationContext}
								hideWhiteBackground={hideWhiteBackground}
								logoSizes={logoSizes}
								onLogoSizeChange={(suggestionId: string, size: number) => {
									setLogoSizes(prev => ({ ...prev, [suggestionId]: size }));
								}}
								onPreviewChange={setPreviewedSuggestionId}
							/>
							<div className={css(bs.py4, bs.px4)}>
								{requiresCompliance ? (
									<Button
										styles={[bs.wFull]}
										onClick={() => {
											history.push({
												pathname: `${url}/compliance-options`,
												state: location.state,
											});
										}}
										disabled={!canSchedule}
									>
										Next: Compliance Options
									</Button>
								) : (
									<Button
										styles={[bs.wFull]}
										onClick={() => scheduleSuggestions(false)}
										disabled={!canSchedule || scheduleMutation.isPending}
										isLoading={scheduleMutation.isPending}
									>
										Schedule on suggested dates
									</Button>
								)}
								<div className={css(styleSheet.advancedOptions)}>
									<Collapsible open={advancedOptionsToggled} onOpenChange={setAdvancedOptionsToggled}>
										<AdvancedOptionsButton
											isCollapsed={!advancedOptionsToggled}
											onClick={() => setAdvancedOptionsToggled(!advancedOptionsToggled)}
										/>
										<CollapsiblePanel>
											<div className={css(bs.flex, bs.gap2)}>
												<Toggle
													id='logo-toggle-multi-scheduler'
													isOn={overlayLogo}
													text={!overlayLogo ? 'Overlay Logo Off' : 'Overlay Logo On'}
													onToggleCheckChanged={() => {
														setOverlayLogo(!overlayLogo);
													}}
													uncheckedColor='#B1B4B6'
												/>
												<TinyPopover
													align='end'
													anchor={<InfoIcon fillColor={charmGray} />}
													autoCloseOtherPopoversOnHover
													dismissOnOutsideAction
													anchorStyles={[styleSheet.infoPopoverToggle]}
													placement={['top', 'right']}
													toggleOnHover
													type={PopoverType.lightBlue}
												>
													<div className={css(styleSheet.popover)}>
														This will overlay your account&apos;s logo on the post&apos;s media at the moment the post
														gets posted. Click on the image preview to see the overlay.
													</div>
												</TinyPopover>
											</div>
											{overlayLogo ? (
												<span className={css(styleSheet.logoSizeLabel)}>
													Logo Size:{' '}
													{previewedSuggestionId && logoSizes[previewedSuggestionId]
														? `${logoSizes[previewedSuggestionId]}px (Adjust per post in preview)`
														: `${Api.DEFAULT_LOGO_SIZE}px (Adjust per post in preview)`}
												</span>
											) : null}
										</CollapsiblePanel>
									</Collapsible>
								</div>
							</div>
						</PrivateRoute>
					</Switch>
				}
			>
				<div className={css(bs.flex, bs.gap4, styleSheet.socialMediaDropdowns)}>
					{socialMediaOptions.map(({ options, provider }) => (
						<MultiCampaignSocialMediaConnection
							options={options}
							selectedOptions={selectedConnectionOptions.filter(option => option.dataContext.type === provider)}
							provider={provider}
							onSelectionChanged={option => {
								const newSelectedConnectionOptions = [...selectedConnectionOptions];
								if (newSelectedConnectionOptions.includes(option)) {
									const index = newSelectedConnectionOptions.indexOf(option);
									newSelectedConnectionOptions.splice(index, 1);
								} else {
									newSelectedConnectionOptions.push(option);
								}
								setSelectedConnectionOptions(newSelectedConnectionOptions);
							}}
							clearSelection={() => {
								const newSelectedConnectionOptions = selectedConnectionOptions.filter(
									x => x.dataContext.type !== provider
								);
								setSelectedConnectionOptions(newSelectedConnectionOptions);
							}}
							key={`social-media-${provider}`}
						/>
					))}
				</div>

				{(hasContentPreferences(accountProfileQuery.data) || accountHasBrandingSettings) && (
					<div className={css(styleSheet.customizeAllContentContainer)}>
						{hasContentPreferences(accountProfileQuery.data) && (
							<AiButton
								title='Make Content Unique with AI'
								disabled={!hasAnyBulkAiEligibleSuggestion(selectedSuggestions) || isParallelBrandingBusy}
								onClick={queueBulkAiOrOpenResetForEditedContent}
								isLoading={isGenerating}
							/>
						)}
						{accountHasBrandingSettings && (
							<>
								<Button
									variant='secondary'
									onClick={() => {
										if (bulkRebrandPostBatches.length === 0) {
											toast.info('No images to rebrand on these posts.');
											return;
										}
										startParallelBranding(bulkRebrandPostBatches);
									}}
									disabled={
										bulkRebrandImageCount === 0 ||
										isParallelBrandingBusy ||
										!parallelBrandingConversationReady ||
										templateQueries.some(q => q.isPending || q.isLoading)
									}
									isLoading={isParallelBrandingBusy}
								>
									Apply branding to all images
								</Button>
								<Button
									variant='secondary'
									onClick={handleUndoBrandingForAll}
									disabled={
										postsWithUndoableBrandingCount === 0 ||
										isParallelBrandingBusy ||
										templateQueries.some(q => q.isPending || q.isLoading)
									}
								>
									Undo all branding
								</Button>
								{parallelBrandingBatchTotal > 0 ? (
									<span className={css(styleSheet.parallelBrandingStatus)}>
										{parallelBrandingSuccessCount}/{parallelBrandingBatchTotal} images branded
										{parallelBrandingFailedCount > 0 ? ` · ${parallelBrandingFailedCount} failed` : ''}
									</span>
								) : null}
								{parallelBrandingFailedCount > 0 && !isParallelBrandingBusy ? (
									<Button variant='secondary' onClick={() => retryParallelBrandingFailed()}>
										Retry failed
									</Button>
								) : null}
							</>
						)}
					</div>
				)}
				<div className={css(bs.flex, bs.flexCol, bs.px6, bs.pt4, bs.pb2, bs.gap8, bs.overflowAuto)}>
					{templateQueries.map((templateQuery, i) => {
						const suggestion = selectedSuggestions[i];
						if (!templateQuery.isSuccess) {
							return <LoadingSpinner key={`${getUniqueIdForSuggestion(suggestion)}-loading`} type='small' />;
						}
						const template = templateQuery.data;

						const scheduledDate = getStartDateForSuggestion(selectedSuggestions[i]);
						const id = getUniqueIdForSuggestion(suggestion);
						const generationFailed = suggestionsWithErrors.some(
							(s: IContentCalendarSelectedSuggestion) => id === getUniqueIdForSuggestion(s)
						);

						let templateWithGeneratedContent = null;
						const shouldShowOriginalContent = showingOriginalContent.includes(id);

						if (generatedContent[id] && !shouldShowOriginalContent) {
							templateWithGeneratedContent = { ...template, content: generatedContent[id] };
						}

						let schedulingState: 'scheduled' | 'failed' | null = null;

						if (scheduledPostsRef.current.has(id)) {
							schedulingState = 'scheduled';
						} else if (Array.from(suggestionPostErrorsRef.current || []).find(([s]) => s === suggestion)) {
							schedulingState = 'failed';
						}
						const isEditing = editingSuggestionId === id;
						// Priority: editedContent > generatedContent > template.content
						const editedTemplateContent = editedContent[id] || generatedContent[id] || template.content;
						const editedTemplateImages = editedImages[id] || template.attachments || [];
						// When not editing, prioritize editedContent over generatedContent
						const displayTemplate = isEditing
							? { ...template, content: editedTemplateContent, attachments: editedTemplateImages }
							: editedContent[id]
								? {
										...template,
										content: editedTemplateContent,
										attachments: editedTemplateImages,
									}
								: templateWithGeneratedContent || {
										...template,
										content: editedTemplateContent,
										attachments: editedTemplateImages,
									};

						const thisPostBrandingBatch =
							rebrandSourceBatchBySuggestion[id] !== undefined
								? rebrandSourceBatchBySuggestion[id]
								: getBrandingBatchForAttachments(editedTemplateImages);
						const isThisPostBrandingActive = Boolean(
							thisPostBrandingBatch?.attachmentIds.some(attachmentId => {
								const p = parallelBrandingSourcePhase[attachmentId];
								return p === 'streaming' || p === 'polling' || p === 'fetching';
							})
						);
						const rebrandSourceFiles = rebrandSourceFilesBySuggestion[id];
						const canUndoBrandingThisPost = Boolean(
							rebrandSourceFiles?.length && !areAttachmentRowsEqual(editedTemplateImages, rebrandSourceFiles)
						);

						return (
							<MultiCampaignTemplateItem
								onUpdateDate={(newDate: Date) => updateStartDateForSuggestion(suggestion, newDate)}
								key={getUniqueIdForSuggestion(suggestion)}
								name={
									<>
										{template?.name || 'Loading...'} {getTemplateApprovalStatus(template?.templateReview)}
									</>
								}
								isEditing={isEditing}
								scheduleDate={scheduledDate}
								onRemove={() => deselectSuggestion(suggestion)}
								error={Array.from(suggestionPostErrorsRef.current || []).find(([s]) => s === suggestion)?.[1]}
								{...sendFromOptions}
								postedOn={shouldShowDuplicateTemplateWarning(template) ? template?.lastUsedByDate?.account : undefined}
								isGeneratingContent={pendingItems.some(item => getUniqueIdForSuggestion(item) === id)}
								isGenerateContentDisabled={isParallelBrandingBusy}
								applyBrandingToPost={
									accountHasBrandingSettings && thisPostBrandingBatch
										? {
												onClick: () => startParallelBranding([thisPostBrandingBatch]),
												isLoading: isThisPostBrandingActive && isParallelBrandingBusy,
												disabled:
													(isParallelBrandingBusy && !isThisPostBrandingActive) ||
													!parallelBrandingConversationReady ||
													templateQuery.isPending ||
													templateQuery.isLoading,
											}
										: undefined
								}
								undoBrandingToPost={
									accountHasBrandingSettings && canUndoBrandingThisPost
										? {
												onClick: () => handleUndoBrandingForPost(suggestion),
												disabled: isParallelBrandingBusy,
											}
										: undefined
								}
								generationFailed={generationFailed}
								onEdit={() => handleEdit(suggestion)}
								onGenerateContent={() => {
									const suggestionId = getUniqueIdForSuggestion(suggestion);
									// Check if this specific suggestion has edited content
									const hasEditedContent = editedContent[suggestionId] || editedImages[suggestionId];
									if (hasEditedContent) {
										setSuggestionIdForReset(suggestionId);
									} else {
										queueItemForAiGeneration(suggestion);
										processQueue();
									}
								}}
								onShowOriginalContent={
									generatedContent[id]
										? () => {
												if (showingOriginalContent.includes(id)) {
													setShowingOriginalContent(prev => prev.filter(showingId => showingId !== id));
												} else {
													setShowingOriginalContent(prev => [...prev, id]);
												}
											}
										: undefined
								}
								showOriginalContent={showingOriginalContent.includes(id)}
								schedulingState={schedulingState}
							>
								<UnsavedChangesWarning
									hasUnsavedChanges={!!editedContent[id] || !!editedImages[id] || !!logoSizes[id]}
									message='You have unsaved changes to this post.'
								/>
								{isEditing ? (
									<EditableSocialMediaTemplateItem
										template={displayTemplate}
										authorId={authorId}
										onContentChange={(content: Api.IRawRichTextContentState) =>
											handleContentChange(suggestion, content)
										}
										onImageSelected={handleImageSelected}
										onVideoSelected={handleVideoSelected}
										onSave={() => handleSaveEdit(suggestion)}
										onCancel={() => handleCancelEdit(suggestion)}
										impersonationContext={impersonationContext}
										postContent={getSocialMediaPlainTextContentFromRawRichContent(editedTemplateContent)}
									/>
								) : (
									<SocialMediaTemplateItem
										key={`socialMedia-template-${template.id}`}
										template={displayTemplate}
										isGeneratingContent={pendingItems.some(item => getUniqueIdForSuggestion(item) === id)}
										videoElement={videoPreviewRef.current}
									/>
								)}
							</MultiCampaignTemplateItem>
						);
					})}
				</div>
			</MultiCampaignSchedulerLayout>

			<MultiSchedulerAiModal
				hasEligibleBulkAiSuggestion={hasAnyBulkAiEligibleSuggestion(selectedSuggestions)}
				onGenerateContentClicked={queueBulkAiOrOpenResetForEditedContent}
				impersonationContext={impersonationContext}
				isCustomizeBlockedByConcurrentWork={isParallelBrandingBusy}
			/>

			<ResetAllEditedContentModal
				isOpen={isResetEditedContentModalOpen}
				onRequestClose={() => setIsResetEditedContentModalOpen(false)}
				isConfirmDisabled={isParallelBrandingBusy}
				onConfirm={() => {
					// Reset edited content and images
					setEditedContent({});
					setEditedImages({});
					setRebrandSourceBatchBySuggestion({});
					setRebrandSourceFilesBySuggestion({});
					setEditingSuggestionId(null);
					setIsResetEditedContentModalOpen(false);
					// Proceed with AI generation
					selectedSuggestions.forEach((suggestion: IContentCalendarSelectedSuggestion) =>
						queueItemForAiGeneration(suggestion, { forBulkAction: true })
					);
					processQueue();
				}}
			/>

			<ResetSuggestionEditedContentModal
				isOpen={suggestionIdForReset !== null}
				onRequestClose={() => setSuggestionIdForReset(null)}
				isConfirmDisabled={isParallelBrandingBusy}
				onConfirm={() => {
					if (suggestionIdForReset) {
						// Reset only this specific suggestion's edited content and images
						setEditedContent(prev => {
							const newState = { ...prev };
							delete newState[suggestionIdForReset];
							return newState;
						});
						setEditedImages(prev => {
							const newState = { ...prev };
							delete newState[suggestionIdForReset];
							return newState;
						});
						setRebrandSourceBatchBySuggestion(prev => {
							const next = { ...prev };
							delete next[suggestionIdForReset];
							return next;
						});
						setRebrandSourceFilesBySuggestion(prev => {
							const next = { ...prev };
							delete next[suggestionIdForReset];
							return next;
						});
						// If this suggestion is being edited, stop editing
						if (editingSuggestionId === suggestionIdForReset) {
							setEditingSuggestionId(null);
						}
						// Find the suggestion and proceed with AI generation
						const suggestion = selectedSuggestions.find(
							(s: IContentCalendarSelectedSuggestion) => getUniqueIdForSuggestion(s) === suggestionIdForReset
						);
						if (suggestion) {
							queueItemForAiGeneration(suggestion);
							processQueue();
						}
						setSuggestionIdForReset(null);
					}
				}}
			/>
		</div>
	);
}

export const MultiSocialScheduler = inject(
	ImpersonationContextKey,
	ModalChildComponentContextKey
)(observer(_MultiSocialScheduler));
